import { mergeOptions } from '../../helpers/common/merge-options';
import { getSelectorElementFromElement } from '../../helpers/dom/get-selector-element-from-element';
import { getTriggerElementsFromElement } from '../../helpers/dom/get-trigger-elements-form-element';
import { EventDispatcher } from '../../helpers/events/event-dispatcher';
import { eventNamespaceFactory } from '../../helpers/events/event-namespace-factory';
import { addTransitionEndListener } from '../../helpers/transition/add-transition-end-listener';

const stringCollapse = 'Collapse';

declare global {
	interface Element {
		[stringCollapse]: Collapse | undefined;
	}
}

export interface CollapseOptions {
	componentClass: string;
	activeClass: string;
	activeParentClass: string;
	showClass: string;
	showClassDesktop: string;
	desktopWidth: number;
	collapsingClass: string;
	transitionDuration: number;
	animateProp: 'height' | 'width' | false;
	isHideable: boolean;
	closeSiblings: boolean;
	useShowClassDesktop: boolean;
	dataParentSelector: string;
	dataTargetSelector: string;
	dataToggleSelector: string;
}

export class Collapse {
	static options: CollapseOptions = {
		activeClass: 'is-active',
		activeParentClass: 'is-active',
		animateProp: 'height',
		collapsingClass: 'collapse--collapsing',
		componentClass: 'collapse',
		showClass: 'collapse--show',
		showClassDesktop: 'collapse--md-show',
		desktopWidth: 1024,
		transitionDuration: 600,
		isHideable: true,
		closeSiblings: false,
		useShowClassDesktop: false,
		dataParentSelector: 'data-parent',
		dataTargetSelector: 'data-target',
		dataToggleSelector: 'data-toggle'
	};

	private options: CollapseOptions;
	private parent: HTMLElement | null;
	private triggers: HTMLElement[];
	private target: Element | undefined;
	private isTransitioning = false;
	public showing = new EventDispatcher();
	public hiding = new EventDispatcher();
	public shown = new EventDispatcher();
	public hidden = new EventDispatcher();
	private eventNamespaceFn = eventNamespaceFactory('opten.collapse');

	constructor(public element: HTMLElement, options?: Partial<CollapseOptions>) {
		this.options = mergeOptions(Collapse, options, element);

		this.parent = getSelectorElementFromElement(
			element,
			this.options.dataParentSelector,
			false
		);

		this.triggers = getTriggerElementsFromElement(this.element, this.options.dataTargetSelector);

		// todo: why do we need that?
		const toggles = Array.from(
			document.querySelectorAll(
				`[${this.options.dataToggleSelector}="collapse"]`
			)
		);
		for (const toggle of toggles) {
			const target = getSelectorElementFromElement(
				toggle,
				this.options.dataTargetSelector
			);
			if (target !== null && target === this.element) {
				this.target = target;
				this.triggers.push(toggle as HTMLElement);
			}
		}

		if (!(stringCollapse in this.element)) {
			// prevent adding event handlers twice
			this.triggers.forEach((trigger) =>
				trigger.addEventListener('click', this.handleClick, false)
			);
		}

		if (this.parent) {
			Array.from(
				this.parent.querySelectorAll(
					`[${this.options.dataToggleSelector}="collapse"][${this.options.dataParentSelector}="#${this.parent.id}"]`
				)
			).forEach((element) => {
				this._addAriaAndCollapsedClass(
					getSelectorElementFromElement(
						element,
						this.options.dataTargetSelector
					),
					[element]
				);
			});
		} else {
			this._addAriaAndCollapsedClass(this.element, this.triggers);
		}

		this.element.classList.add(this.options.componentClass);

		this.element[stringCollapse] = this;
	}

	static getInstance(
		element: HTMLElement,
		options?: CollapseOptions,
		create = true
	) {
		let data = element[stringCollapse] as Collapse;

		if (!data && create) {
			data = new Collapse(element, options);
		}

		return data;
	}

	isOpen() {
		if (
			this.options.useShowClassDesktop &&
			window.outerWidth >= this.options.desktopWidth &&
			this.element.classList.contains(this.options.showClassDesktop)
		) {
			this.element.classList.add(this.options.showClass);
		}
		this.element.classList.remove(this.options.showClassDesktop);
		return this.element.classList.contains(this.options.showClass);
	}

	toggle() {
		if (this.isOpen()) {
			this.hide();
		} else {
			this.show();
		}
	}

	show() {
		if (this.isTransitioning || this.isOpen()) {
			return;
		}

		if (this.options.closeSiblings && this.parent) {
			const selector = `[${this.options.dataParentSelector}="#${this.parent.id}"]`;
			const actives = Array.from(
				this.parent.querySelectorAll(
					`${selector}.${this.options.showClass}, ${selector}.${this.options.collapsingClass}`
				)
			).filter((active) => active !== this.target);

			if (actives && actives.length) {
				const anyTransitioning = actives.filter((active) => {
					const collapseInstance = active[stringCollapse] as Collapse;
					return collapseInstance && collapseInstance.isTransitioning;
				});

				if (anyTransitioning.length) {
					return;
				}

				actives.forEach((active) =>
					Collapse.getInstance(active as HTMLElement).hide()
				);
			}
		}

		this._handleShow(this.element, this.triggers);
	}

	hide() {
		if (this.isTransitioning || !this.isOpen() || !this.options.isHideable) {
			return;
		}

		this._handleClose(this.element, this.triggers);
	}

	destroy() {
		const element = this.element;

		if (element[stringCollapse]) {
			element[stringCollapse] = undefined;

			element.classList.remove(this.options.componentClass);
			element.classList.remove(this.options.collapsingClass);
			element.classList.remove(this.options.showClass);

			if (this.options.animateProp) {
				element.style[this.options.animateProp] = '';
			}
		}
	}

	private handleClick = (event: Event) => {
		// preventDefault only for <a> elements (which change the URL) not inside the collapsible element
		const trigger = event?.currentTarget as HTMLElement;
		if (trigger && trigger.tagName === 'A') {
			event.preventDefault();
		}

		const target = getSelectorElementFromElement(
			trigger,
			this.options.dataTargetSelector
		);
		let data = target ? target[stringCollapse] as Collapse : null;

		if (!data) {
			data = trigger[stringCollapse] as Collapse;
		}

		if (target) {
			Collapse.getInstance(target, data.options).toggle();
		}
	};

	private _handleShow(collapseElement: HTMLElement, triggers?: Element[]) {

		this.showing.dispatch();
		this.eventNamespaceFn(collapseElement, 'show');

		collapseElement.classList.remove(this.options.componentClass);
		collapseElement.classList.add(this.options.collapsingClass);
		if (this.options.animateProp) {
			collapseElement.style[this.options.animateProp] =
				collapseElement.scrollHeight + 'px';
		}

		if (triggers && triggers.length) {
			triggers.forEach((trigger) => {
				trigger.classList.add(this.options.activeClass);
				trigger.setAttribute('aria-expanded', 'true');
			});
		}

		if (this.parent) {
			this.parent.classList.add(this.options.activeClass);
		}

		this.isTransitioning = true;

		addTransitionEndListener(
			collapseElement,
			() => {
				collapseElement.classList.remove(this.options.collapsingClass);
				collapseElement.classList.add(this.options.componentClass);
				collapseElement.classList.add(this.options.showClass);

				this.isTransitioning = false;
				if (this.options.animateProp) {
					collapseElement.style[this.options.animateProp] = '';
				}

				this.shown.dispatch();
				this.eventNamespaceFn(collapseElement, 'shown');
			},
			this.options.transitionDuration
		);
	}

	private _handleClose(collapseElement: HTMLElement, triggers?: Element[]) {
		this.hiding.dispatch();
		this.eventNamespaceFn(collapseElement, 'hide');

		if (this.options.animateProp) {
			collapseElement.style[this.options.animateProp] =
				collapseElement.scrollHeight + 'px';
		}

		collapseElement.classList.add(this.options.collapsingClass);
		collapseElement.classList.remove(this.options.componentClass);
		collapseElement.classList.remove(this.options.showClass);
		// force reflow to enable transition
		collapseElement.offsetWidth; // eslint-disable-line

		if (triggers && triggers.length) {
			triggers.forEach((trigger) => {
				const target = getSelectorElementFromElement(
					trigger,
					this.options.dataTargetSelector
				);

				if (target !== null) {
					if (!target.classList.contains(this.options.showClass)) {
						trigger.classList.remove(this.options.activeClass);
						trigger.setAttribute('aria-expanded', 'false');
					}
				}
			});
		}

		if (this.parent) {
			const selector = `[${this.options.dataParentSelector}="#${this.parent.id}"]`;
			const actives = Array.from(
				this.parent.querySelectorAll(
					`${selector}.${this.options.showClass}, ${selector}.${this.options.collapsingClass}`
				)
			).filter((o) => o !== collapseElement);

			if (this.parent && actives.length === 0) {
				this.parent.classList.remove(this.options.activeParentClass);
			}
		}

		this.isTransitioning = true;
		if (this.options.animateProp) {
			collapseElement.style[this.options.animateProp] = '';
		}

		addTransitionEndListener(
			collapseElement,
			() => {
				this.isTransitioning = false;

				collapseElement.classList.remove(this.options.collapsingClass);
				collapseElement.classList.add(this.options.componentClass);

				if (this.options.animateProp) {
					collapseElement.style[this.options.animateProp] = '';
				}

				this.hidden.dispatch();
				this.eventNamespaceFn(collapseElement, 'hidden');
			},
			this.options.transitionDuration
		);
	}

	private _addAriaAndCollapsedClass(
		element: Element | null,
		triggers: Element[]
	) {
		if (element) {
			const isOpen = element.classList.contains(this.options.showClass);

			if (triggers && triggers.length) {
				triggers.forEach((trigger) => {
					isOpen
						? trigger.classList.add(this.options.activeClass)
						: trigger.classList.remove(this.options.activeClass);

					trigger.setAttribute(
						'aria-expanded',
						isOpen.toString().toLowerCase()
					);
				});
			}
		}
	}
}
